import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, Loader2, TrendingUp } from "lucide-react";

export default function MyNegotiations() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOffers, setNewOffers] = useState({});
  const [counterPrices, setCounterPrices] = useState({});
  const [aiSuggestions, setAiSuggestions] = useState({});
  const [aiLoading, setAiLoading] = useState({});
  const [bookingBusy, setBookingBusy] = useState({});

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/negotiations/mine");
    setItems(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const respond = async (id, action, counter_price) => {
    try {
      await api.post(`/negotiations/${id}/respond`, { action, counter_price });
      toast.success(`Negotiation ${action}ed`);
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const bookAndPay = async (n) => {
    setBookingBusy((b) => ({ ...b, [n.negotiation_id]: true }));
    try {
      const { data } = await api.post(`/negotiations/${n.negotiation_id}/book`, {
        rooms: n.rooms_needed || 1,
        checkin: n.checkin,
        checkout: n.checkout,
      });
      if (data.booking_id) navigate(`/pay/booking/${data.booking_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start booking");
    } finally {
      setBookingBusy((b) => ({ ...b, [n.negotiation_id]: false }));
    }
  };

  const customerAction = async (id, action, offer_price) => {
    try {
      await api.post(`/negotiations/${id}/customer-action`, { action, offer_price });
      toast.success("Action sent");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const aiSuggest = async (id) => {
    setAiLoading((s) => ({ ...s, [id]: true }));
    try {
      const { data } = await api.post(`/negotiations/${id}/ai-suggest`);
      setAiSuggestions((s) => ({ ...s, [id]: data }));
      setCounterPrices((p) => ({ ...p, [id]: data.suggested_counter }));
      toast.success(`AI suggests ₹${data.suggested_counter.toLocaleString("en-IN")}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI suggestion failed");
    } finally {
      setAiLoading((s) => ({ ...s, [id]: false }));
    }
  };

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)]">
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">
        <div className="ss-label text-[#A6852F]">Negotiations</div>
        <h1 className="font-head text-4xl md:text-5xl font-black text-slate-900 mt-3">
          {user?.role === "hotel_partner" ? "Customer Offers" : (<>Your <span className="ss-underline-gold text-[#A6852F]">Negotiations</span></>)}
        </h1>

        {loading ? <div className="mt-10"><span className="hw-pulse-dot" /></div> : items.length === 0 ? (
          <div className="rounded-2xl bg-white border border-slate-200 p-10 mt-8 text-center">
            <p className="text-slate-600 font-body">No negotiations yet.</p>
          </div>
        ) : (
          <div className="space-y-4 mt-8" data-testid="negotiations-list">
            {items.map((n) => (
              <div key={n.negotiation_id} className="rounded-2xl bg-white border border-slate-200 hover:border-[#C8A34D] hover:shadow-[0_20px_50px_-24px_rgba(200,163,77,0.35)] transition-all p-6" data-testid={`negotiation-${n.negotiation_id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="ss-label text-slate-500">{user?.role === "hotel_partner" ? "From" : "Hotel"}</div>
                    <h3 className="font-head font-bold text-xl text-slate-900">{user?.role === "hotel_partner" ? n.customer_name : n.hotel_name}</h3>
                    <div className="text-sm text-slate-500 mt-1 font-body">{n.checkin} → {n.checkout} · {n.rooms_needed} rooms</div>
                  </div>
                  <StatusPill status={n.status} />
                </div>

                <div className="mt-5 grid grid-cols-3 gap-3">
                  <PriceBox label="Listed" value={n.initial_price} />
                  <PriceBox label="Customer Offer" value={n.offer_price} accent />
                  {n.counter_price ? <PriceBox label="Hotel Counter" value={n.counter_price} dark /> : <div className="bg-slate-50 p-3 border border-slate-200 grid place-items-center text-slate-400 hw-label">—</div>}
                </div>

                {n.history?.length > 0 && (
                  <div className="mt-5 border-t border-slate-100 pt-4">
                    <div className="hw-label text-slate-500 mb-2">Timeline</div>
                    <div className="space-y-1 text-xs hw-mono text-slate-600">
                      {n.history.map((h, i) => (
                        <div key={i}>{h.by} · {h.type}{h.price ? ` ₹${h.price}` : ""}</div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action buttons */}
                {user?.role === "hotel_partner" && n.status === "pending" && (
                  <div className="mt-5 space-y-3">
                    {aiSuggestions[n.negotiation_id] && (
                      <div className="bg-slate-900 text-white p-4 rounded-sm" data-testid={`ai-result-${n.negotiation_id}`}>
                        <div className="flex items-start gap-3">
                          <Sparkles className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="hw-label text-emerald-400">AI suggested counter</span>
                              <span className="hw-chip border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hw-label">
                                <TrendingUp className="w-3 h-3" /> {aiSuggestions[n.negotiation_id].accept_probability}% acceptance
                              </span>
                            </div>
                            <div className="hw-head text-3xl font-black text-emerald-400 mt-1">
                              ₹{aiSuggestions[n.negotiation_id].suggested_counter.toLocaleString("en-IN")}
                            </div>
                            <p className="text-sm text-slate-300 mt-2 leading-snug">{aiSuggestions[n.negotiation_id].reasoning}</p>
                            {aiSuggestions[n.negotiation_id].sweetener && (
                              <div className="hw-label text-emerald-300 mt-2">+ Add: {aiSuggestions[n.negotiation_id].sweetener}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                      <Button onClick={() => respond(n.negotiation_id, "accept")} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold" data-testid={`accept-${n.negotiation_id}`}>Accept ₹{n.offer_price}</Button>
                      <Button onClick={() => respond(n.negotiation_id, "reject")} variant="outline" className="rounded-sm border-red-300 text-red-700 hover:bg-red-50" data-testid={`reject-${n.negotiation_id}`}>Reject</Button>
                      <Input
                        type="number"
                        placeholder="Counter ₹"
                        value={counterPrices[n.negotiation_id] || ""}
                        onChange={(e) => setCounterPrices({ ...counterPrices, [n.negotiation_id]: e.target.value })}
                        className="rounded-sm w-32"
                        data-testid={`counter-input-${n.negotiation_id}`}
                      />
                      <Button
                        onClick={() => aiSuggest(n.negotiation_id)}
                        disabled={aiLoading[n.negotiation_id]}
                        variant="outline"
                        className="rounded-sm border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-emerald-400 font-bold"
                        data-testid={`ai-suggest-${n.negotiation_id}`}
                      >
                        {aiLoading[n.negotiation_id] ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                        AI Suggest
                      </Button>
                      <Button onClick={() => respond(n.negotiation_id, "counter", Number(counterPrices[n.negotiation_id]))} className="bg-emerald-600 hover:bg-emerald-700 text-slate-900 rounded-sm font-bold" data-testid={`counter-${n.negotiation_id}`}>Send Counter</Button>
                    </div>
                  </div>
                )}
                {user?.role === "customer" && n.status === "accepted" && (
                  <div className="mt-5" data-testid={`accepted-book-block-${n.negotiation_id}`}>
                    <div className="p-4 bg-emerald-50 border border-emerald-300 rounded-sm mb-3">
                      <div className="hw-label text-emerald-800">Great — offer accepted at ₹{(n.final_price || n.offer_price).toLocaleString("en-IN")}/room</div>
                      <div className="text-sm text-slate-700 mt-1">Complete payment to lock your reservation. Invoice + confirmation will be sent to your email &amp; WhatsApp.</div>
                    </div>
                    <Button
                      onClick={() => bookAndPay(n)}
                      disabled={bookingBusy[n.negotiation_id]}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold py-6 h-auto px-8"
                      data-testid={`book-pay-${n.negotiation_id}`}
                    >
                      {bookingBusy[n.negotiation_id] ? "Preparing…" : `Book & Pay ₹${(n.final_price || n.offer_price).toLocaleString("en-IN")}/room`}
                    </Button>
                  </div>
                )}

                {user?.role === "customer" && n.status === "countered" && (
                  <div className="mt-5 flex flex-wrap items-center gap-2">
                    <Button onClick={() => customerAction(n.negotiation_id, "accept_counter")} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold" data-testid={`accept-counter-${n.negotiation_id}`}>Accept ₹{n.counter_price}</Button>
                    <Input
                      type="number"
                      placeholder="New offer ₹"
                      value={newOffers[n.negotiation_id] || ""}
                      onChange={(e) => setNewOffers({ ...newOffers, [n.negotiation_id]: e.target.value })}
                      className="rounded-sm w-32"
                      data-testid={`new-offer-input-${n.negotiation_id}`}
                    />
                    <Button onClick={() => customerAction(n.negotiation_id, "new_offer", Number(newOffers[n.negotiation_id]))} className="bg-emerald-600 hover:bg-emerald-700 text-slate-900 rounded-sm font-bold" data-testid={`new-offer-${n.negotiation_id}`}>Send new offer</Button>
                  </div>
                )}

                <div className="mt-4 text-right">
                  <Link to={`/hotels/${n.hotel_id}`} className="hw-label text-blue-700">View hotel →</Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PriceBox({ label, value, accent, dark }) {
  return (
    <div className={`p-3 border rounded-xl ${accent ? "border-[#C8A34D] bg-[#FBF3DA]" : dark ? "bg-[#0F1115] text-[#E8D8A8] border-[#0F1115]" : "bg-slate-50 border-slate-200"}`}>
      <div className={`ss-label ${accent ? "text-[#A6852F]" : dark ? "text-[#E8D8A8]" : "text-slate-500"}`}>{label}</div>
      <div className={`font-head text-xl font-black ${dark ? "text-[#E8D8A8]" : "text-slate-900"}`}>₹{value?.toLocaleString("en-IN")}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending:   ["bg-[#FBF3DA] text-[#A6852F] border-[#C8A34D]/40", "Pending"],
    accepted:  ["bg-[#E7F7EF] text-[#0F5934] border-[#16A34A]/40", "Accepted"],
    rejected:  ["bg-red-50 text-red-700 border-red-200",           "Rejected"],
    countered: ["bg-[#0F1115] text-[#E8D8A8] border-[#0F1115]",    "Counter sent"],
  };
  const [cls, txt] = map[status] || ["bg-slate-100 text-slate-700 border-slate-300", status];
  return <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wide ${cls}`}>{txt}</span>;
}
